import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  Sparkles, ArrowLeft, ArrowRight, Heart, Copy, Check, Gift,
  Users, Share2, Star,
} from "lucide-react";
import { toast } from "sonner";
import { getReferralDashboard } from "@/lib/api";
import SocialShareButtons from "@/components/earthpulse/SocialShareButtons";

/**
 * ReferralPage — the warm "thank you" landing page for FRIEND-XXXXXX code
 * owners. Route: `/friend/:code`.
 *
 * Shows the referrer:
 *  - How many friends they've helped discover Earth Pulse
 *  - How many THANKS-YYYYY reward codes they've earned (& unused ones)
 *  - A prominent copy-block for their referral code + a share sheet
 *
 * Data lives at GET /api/referral/dashboard/:code and returns no PII beyond
 * a first name — so this page is safely public-linkable.
 */
export default function ReferralPage() {
  const { code } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!code) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const r = await getReferralDashboard(code);
        if (!cancelled) setData(r);
      } catch (err) {
        if (!cancelled) {
          const status = err?.response?.status;
          setError(
            status === 404
              ? "That code doesn't ring a bell."
              : (err?.response?.data?.detail || err?.message || "Could not load referral data"),
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [code]);

  const copyCode = async (val) => {
    try {
      await navigator.clipboard.writeText(val);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
      toast.success("Code copied", { description: "Paste it wherever your friend can find it." });
    } catch {
      toast.error("Couldn't copy — please copy the code manually.");
    }
  };

  if (loading) return <Loading />;
  if (error) return <NotFound message={error} />;
  if (!data) return <NotFound message="No referral data found." />;

  const {
    code: refCode,
    first_name,
    discount_pct,
    friends_helped,
    max_uses,
    uses_left,
    rewards_earned,
    rewards_unused,
    reward_codes,
  } = data;

  const greeting = first_name ? `${first_name},` : "Hello, quiet one —";
  const shareUrl = `${window.location.origin}/subscribe?ref=${encodeURIComponent(refCode)}`;
  const shareText = `Come see what Earth Pulse is — daily cosmic guidance for the ones who feel it first. Use my code ${refCode} for ${discount_pct}% off.`;

  return (
    <div className="min-h-screen relative z-10" data-testid="referral-page">
      {/* ── Header ─────────────────────────────────────────────── */}
      <header className="border-b border-[color:var(--gold)]/15">
        <div className="mx-auto max-w-[1100px] px-6 md:px-10 py-6">
          <Link to="/" className="flex items-center gap-3 group w-fit" data-testid="referral-back">
            <ArrowLeft size={16} className="text-[color:var(--gold)] group-hover:-translate-x-0.5 transition-transform" />
            <span className="label-overline">Back to Earth Pulse</span>
          </Link>
        </div>
      </header>

      <main className="mx-auto max-w-[900px] px-6 md:px-10 py-14 md:py-20">
        {/* ── Hero ───────────────────────────────────────────── */}
        <div className="text-center max-w-2xl mx-auto stagger-in">
          <div className="label-overline flex items-center justify-center gap-2">
            <Heart size={11} className="text-[color:var(--rose-gold)]" /> A quiet thank-you
          </div>
          <h1
            data-testid="referral-hero-title"
            className="mt-5 font-serif-display text-[color:var(--parchment)] text-4xl md:text-6xl leading-[1.05] tracking-tight"
          >
            {greeting}{" "}
            <span className="font-serif-italic text-[color:var(--gold)]">
              {friends_helped > 0 ? "look what you've done." : "your code is ready."}
            </span>
          </h1>
          <p className="mt-6 font-serif-italic text-[color:var(--parchment-soft)] text-lg md:text-xl leading-relaxed">
            {friends_helped > 0
              ? `You've quietly sent ${friends_helped} ${friends_helped === 1 ? "person" : "people"} the way of Earth Pulse. That's ${friends_helped === 1 ? "a soul" : "souls"} noticing the sky a little more because of you.`
              : "Share your code below — every friend who joins earns you a soft thank-you credit toward Earth Pulse."}
          </p>
        </div>

        {/* ── Stat trio ──────────────────────────────────────── */}
        <div className="mt-14 grid grid-cols-2 md:grid-cols-3 gap-4 md:gap-6 stagger-in" data-testid="referral-stats">
          <StatCard
            icon={Users}
            label="Friends helped"
            value={friends_helped}
            testid="referral-stat-friends"
          />
          <StatCard
            icon={Gift}
            label={rewards_earned === 1 ? "Thanks-credit earned" : "Thanks-credits earned"}
            value={rewards_earned}
            accent="rose"
            testid="referral-stat-rewards"
          />
          <StatCard
            icon={Star}
            label="Uses left on your code"
            value={uses_left}
            sub={`of ${max_uses}`}
            hideOnMobile
            testid="referral-stat-uses-left"
          />
        </div>

        {/* ── Your code ──────────────────────────────────────── */}
        <section className="mt-14 parchment-card rounded-3xl p-8 md:p-12 stagger-in" data-testid="referral-code-block">
          <div className="label-overline text-[color:var(--gold)]">Your referral code</div>
          <p className="mt-3 font-serif-italic text-[color:var(--text-secondary)] text-sm max-w-lg">
            Send this to a friend. They get {discount_pct}% off their first Earth Pulse purchase — you get a warm thank-you back.
          </p>
          <div className="mt-6 flex flex-col sm:flex-row items-stretch sm:items-center gap-4">
            <div
              className="flex-1 font-serif-display text-[color:var(--parchment)] text-3xl md:text-4xl tracking-widest bg-[color:var(--bg-base)]/60 border border-[color:var(--gold)]/30 rounded-2xl px-5 py-4 text-center"
              data-testid="referral-code-display"
            >
              {refCode}
            </div>
            <button
              type="button"
              onClick={() => copyCode(refCode)}
              className="inline-flex items-center justify-center gap-2 px-6 py-4 rounded-full bg-[color:var(--gold)] hover:bg-[color:var(--gold-soft)] text-[color:var(--bg-base)] font-medium transition"
              data-testid="referral-copy-code"
            >
              {copied ? <Check size={16} /> : <Copy size={16} />}
              {copied ? "Copied" : "Copy code"}
            </button>
          </div>

          <div className="mt-10">
            <div className="label-overline text-[10px] mb-4 flex items-center gap-2">
              <Share2 size={11} /> Share in one tap
            </div>
            <SocialShareButtons
              url={shareUrl}
              text={shareText}
              testIdPrefix="referral-share"
            />
          </div>
        </section>

        {/* ── Reward codes ───────────────────────────────────── */}
        {rewards_earned > 0 && (
          <section className="mt-12 parchment-card rounded-3xl p-8 md:p-12 stagger-in" data-testid="referral-rewards-block">
            <div className="flex items-start justify-between flex-wrap gap-3 mb-6">
              <div>
                <div className="label-overline text-[color:var(--rose-gold)] flex items-center gap-2">
                  <Gift size={11} /> Your thank-you credits
                </div>
                <h2 className="mt-2 font-serif-display text-[color:var(--parchment)] text-2xl md:text-3xl tracking-tight">
                  {rewards_unused > 0
                    ? `${rewards_unused} unused ${rewards_unused === 1 ? "credit" : "credits"} waiting for you.`
                    : "All credits redeemed — you legend."}
                </h2>
              </div>
            </div>

            <ul className="space-y-3">
              {reward_codes.map((rw) => (
                <li
                  key={rw.code}
                  className="flex items-center justify-between gap-3 py-3 px-4 rounded-xl border border-[color:var(--gold)]/15 hover:border-[color:var(--gold)]/40 transition"
                  data-testid={`referral-reward-${rw.code}`}
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <span
                      className={`shrink-0 w-2 h-2 rounded-full ${rw.used ? "bg-[color:var(--text-muted)]" : "bg-[color:var(--rose-gold)] animate-pulse"}`}
                    />
                    <span className="font-serif-display text-[color:var(--parchment)] text-lg md:text-xl tracking-widest truncate">
                      {rw.code}
                    </span>
                    <span
                      className={`shrink-0 label-overline text-[10px] ${rw.used ? "text-[color:var(--text-muted)]" : "text-[color:var(--rose-gold)]"}`}
                    >
                      {rw.used ? "used" : `${rw.discount_pct}% off`}
                    </span>
                  </div>
                  {!rw.used && (
                    <button
                      type="button"
                      onClick={() => copyCode(rw.code)}
                      className="shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[color:var(--gold)] hover:bg-[color:var(--gold)]/10 text-xs font-medium transition"
                      data-testid={`referral-copy-reward-${rw.code}`}
                    >
                      <Copy size={11} /> copy
                    </button>
                  )}
                </li>
              ))}
            </ul>
          </section>
        )}

        {/* ── Footer CTA ─────────────────────────────────────── */}
        <div className="mt-12 text-center stagger-in">
          <Link
            to="/almanac"
            className="inline-flex items-center gap-2 font-serif-italic text-[color:var(--gold)] hover:underline"
            data-testid="referral-return-almanac"
          >
            Return to today&apos;s guidance <ArrowRight size={13} />
          </Link>
        </div>
      </main>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, sub, accent, testid, hideOnMobile }) {
  const color = accent === "rose" ? "var(--rose-gold)" : "var(--gold)";
  return (
    <div
      className={`parchment-card rounded-2xl p-5 md:p-7 text-center ${hideOnMobile ? "hidden md:block" : ""}`}
      data-testid={testid}
    >
      <div className="mx-auto w-9 h-9 rounded-full border flex items-center justify-center" style={{ borderColor: `${color === "var(--rose-gold)" ? "rgba(229,168,124,0.4)" : "rgba(212,162,76,0.4)"}` }}>
        <Icon size={14} style={{ color }} />
      </div>
      <div className="mt-4 font-serif-display text-[color:var(--parchment)] text-4xl md:text-5xl tracking-tight">
        {value}
      </div>
      <div className="mt-2 label-overline text-[10px]">
        {label}{sub ? <span className="font-serif-italic normal-case text-[color:var(--text-muted)] tracking-normal ml-1">· {sub}</span> : null}
      </div>
    </div>
  );
}

function Loading() {
  return (
    <div className="min-h-screen flex items-center justify-center px-6">
      <div className="text-center max-w-md" data-testid="referral-loading">
        <div className="mx-auto w-14 h-14 rounded-full border border-[color:var(--gold)]/50 flex items-center justify-center animate-pulse">
          <Sparkles size={22} className="text-[color:var(--gold)]" />
        </div>
        <h1 className="mt-8 font-serif-display text-3xl md:text-4xl text-[color:var(--parchment)] tracking-tight">
          Loading your thank-you page…
        </h1>
      </div>
    </div>
  );
}

function NotFound({ message }) {
  return (
    <div className="min-h-screen flex items-center justify-center px-6" data-testid="referral-not-found">
      <div className="text-center max-w-md">
        <Sparkles size={26} className="text-[color:var(--gold)] mx-auto mb-4" />
        <h1 className="font-serif-display text-[color:var(--parchment)] text-2xl md:text-3xl mb-3">
          {message}
        </h1>
        <p className="font-serif-italic text-[color:var(--parchment-soft)] mb-6">
          Double-check the code, or head back home and try again.
        </p>
        <Link
          to="/"
          className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[color:var(--gold)] hover:bg-[color:var(--gold-soft)] text-[color:var(--bg-base)] font-medium transition"
        >
          Back to Earth Pulse <ArrowRight size={13} />
        </Link>
      </div>
    </div>
  );
}
